import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import DataGrid, { Column } from "devextreme-react/data-grid";
import { TextBox, Button as TextBoxButton } from "devextreme-react/text-box";
import TagBox from "../../common/CustomTagBox";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { baseRequest } from "../../../fetch/basicThunk";
import { convertToLocalTime } from "../../../commonFunctions/commonFunctions";
import checkmarkFrameIcon from "../../../images/checkmark-frame.svg";
import abortFrameIcon from "../../../images/abort-frame.svg";
import editIcon from "../../../images/edit.svg";
import clipboardListIcon from "../../../images/clipboard-list.svg";
import copyIcon from "../../../images/copy.svg";
import searchIcon from "../../../images/ui-ic-search.svg";
import addIconWhite from "../../../images/add-icon-white.svg";
import warningIcon from "../../../images/warningIcon.svg";
import uiIcAlertError from "../../../images/ui-ic-alert-error.svg";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CreatePaymentOrderModal from "./CreatePaymentOrderModal";
import ApprovePaymentOrderModal from "./ApprovePaymentOrderModal";
import RejectPaymentOrderModal from "./RejectPaymentOrderModal";
import ApprovalHistory from "./ApprovalHistory";
import GeneralBillingHistory from "./GeneralBillingHistory";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { openModal } from "../../../redux/modal/modalSlice";
import {
  QUERY_GET_BILLING_TICKETS_BY_CASE_ID,
  QUERY_GET_BILLING_PAYMENT_ORDERS,
  QUERY_GET_BILLING_CASE_HISTORY,
  QUERY_PUT_BILLING_TICKET,
} from "../../../fetch/constantQueries";

dayjs.extend(utc);
dayjs.extend(timezone);

const useStyles = makeStyles({
  container: {
    minHeight: "15rem",
    padding: "1rem 0",
    width: "100%",
    boxSizing: "border-box",
  },
  bigNumbersWrapper: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "0.75rem",
    marginBottom: "1rem",
    width: "100%",
    boxSizing: "border-box",
  },
  bigNumberCardBlue: {
    backgroundColor: "#007BC0",
    borderRadius: 0,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
    boxSizing: "border-box",
  },
  bigNumberCardWhite: {
    backgroundColor: "#fff",
    borderRadius: 0,
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
    boxSizing: "border-box",
  },
  bigNumberTitleBlue: {
    fontSize: "0.65rem",
    fontWeight: "normal",
    color: "#ffffff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: "0.25rem",
  },
  bigNumberTitleWhite: {
    fontSize: "0.65rem",
    fontWeight: "normal",
    color: "#555555",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: "0.25rem",
  },
  bigNumberValueBlue: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "#ffffff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  bigNumberValueWhite: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "#000000",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  tableCard: {
    backgroundColor: "#fff",
    width: "100%",
    boxSizing: "border-box",
  },
  cardHeader: {
    padding: "12px 16px",
  },
  cardTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "#000000",
  },
  cardDivider: {
    width: "100%",
    borderBottom: "1px solid #e0e0e0",
  },
  tableToolbar: {
    padding: "14px 16px 0 16px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.75rem",
  },
  compactToolbarInput: {
    height: "38px !important",
    fontSize: "0.875rem",
    "& .dx-texteditor-container": {
      height: "38px !important",
      display: "flex !important",
      alignItems: "center !important",
      position: "relative !important",
    },
    "&.dx-texteditor-empty:not(.dx-state-focused) .dx-texteditor-label": {
      top: "50% !important",
      marginTop: "-8px !important",
      height: "16px !important",
      lineHeight: "16px !important",
      fontSize: "0.875rem !important",
    },
    "&.dx-texteditor-empty:not(.dx-state-focused) .dx-texteditor-label .dx-label span": {
      fontSize: "0.875rem !important",
      lineHeight: "16px !important",
    },
    "& .dx-texteditor-label": {
      top: "2px !important",
      height: "12px !important",
      lineHeight: "12px !important",
      fontSize: "0.6875rem !important",
      pointerEvents: "none !important",
    },
    "& .dx-texteditor-label .dx-label span": {
      fontSize: "0.6875rem !important",
      lineHeight: "12px !important",
      color: "#555555",
    },
    "&.dx-state-focused .dx-texteditor-label .dx-label span": {
      color: "#007BC0 !important",
    },
    "& .dx-placeholder, & .dx-placeholder::before": {
      display: "none !important",
    },
    "& .dx-tag-container": {
      minHeight: "unset !important",
      height: "38px !important",
      maxHeight: "38px !important",
      paddingTop: "14px !important",
      paddingBottom: "3px !important",
      paddingLeft: "6px !important",
      display: "flex !important",
      alignItems: "flex-end !important",
      flexWrap: "nowrap !important",
      boxSizing: "border-box !important",
      overflow: "hidden !important",
    },
    "& .dx-texteditor-input-container": {
      minHeight: "unset !important",
      height: "38px !important",
      display: "flex !important",
      alignItems: "flex-end !important",
      boxSizing: "border-box !important",
    },
    "& .dx-texteditor-input": {
      fontSize: "0.875rem",
      paddingTop: "0 !important",
      paddingBottom: "3px !important",
      minHeight: "unset !important",
      height: "20px !important",
      boxSizing: "border-box",
    },
    "& .dx-tag-container .dx-texteditor-input": {
      minHeight: "unset !important",
      height: "19px !important",
      fontSize: "0.75rem !important",
      padding: "0 !important",
      margin: "0 !important",
      lineHeight: "19px !important",
    },
    "& .dx-tag": {
      height: "19px !important",
      maxHeight: "19px !important",
      margin: "0 2px !important",
      fontSize: "0.75rem !important",
      display: "inline-flex !important",
      alignItems: "center !important",
      backgroundColor: "#e0e2e5 !important",
      borderRadius: "3px !important",
      maxWidth: "calc(100% - 4px) !important",
      boxSizing: "border-box !important",
    },
    "& .dx-tag-content": {
      position: "relative !important",
      padding: "0 4px 0 8px !important",
      fontSize: "0.75rem !important",
      lineHeight: "19px !important",
      display: "flex !important",
      alignItems: "center !important",
      boxSizing: "border-box !important",
      maxWidth: "100% !important",
    },
    "& .dx-tag-content > span": {
      overflow: "hidden !important",
      textOverflow: "ellipsis !important",
      whiteSpace: "nowrap !important",
      flex: "1 1 auto !important",
      minWidth: 0,
    },
    "& .dx-tag-remove-button": {
      position: "relative !important",
      top: "unset !important",
      right: "unset !important",
      width: "12px !important",
      height: "12px !important",
      minWidth: "12px !important",
      marginLeft: "6px !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
      cursor: "pointer !important",
      flexShrink: "0 !important",
    },
    "& .dx-tag-remove-button::before": {
      content: '"" !important',
      position: "absolute !important",
      top: "50% !important",
      left: "50% !important",
      width: "1.5px !important",
      height: "7px !important",
      transform: "translate(-50%, -50%) rotate(45deg) !important",
      backgroundColor: "#666666 !important",
      margin: "0 !important",
      right: "auto !important",
    },
    "& .dx-tag-remove-button::after": {
      content: '"" !important',
      position: "absolute !important",
      top: "50% !important",
      left: "50% !important",
      width: "7px !important",
      height: "1.5px !important",
      transform: "translate(-50%, -50%) rotate(45deg) !important",
      backgroundColor: "#666666 !important",
      margin: "0 !important",
      right: "auto !important",
    },
    "& .dx-tag-remove-button:hover::before, & .dx-tag-remove-button:hover::after": {
      backgroundColor: "#000000 !important",
    },
    "& .dx-texteditor-buttons-container": {
      height: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
    },
    "& .dx-texteditor-buttons-container .dx-button": {
      height: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
    },
    "& .dx-texteditor-buttons-container .dx-button .dx-button-content": {
      height: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
      padding: "0 !important",
    },
    "& .dx-dropdowneditor-button": {
      height: "100% !important",
      width: "28px !important",
      minWidth: "28px !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
      padding: "0 !important",
      margin: "0 !important",
    },
    "& .dx-dropdowneditor-button .dx-button-content": {
      height: "100% !important",
      width: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
      padding: "0 !important",
    },
    "& .dx-dropdowneditor-button .dx-button-content img": {
      height: "0.75rem !important",
      width: "0.75rem !important",
      objectFit: "contain",
      display: "block",
    },
    "&.dx-dropdowneditor-active .dx-dropdowneditor-button, &.dx-state-focused .dx-dropdowneditor-button": {
      height: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
    },
    "&.dx-dropdowneditor-active .dx-dropdowneditor-button .dx-button-content, &.dx-state-focused .dx-dropdowneditor-button .dx-button-content": {
      height: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
    },
    "& .dx-clear-button": {
      height: "100% !important",
      width: "24px",
      minWidth: "24px",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
    },
    "& .dx-clear-button .dx-button-content": {
      height: "100% !important",
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center !important",
    },
  },
  addOrderButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    height: "38px",
    padding: "0 14px",
  },
  detailsActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "0.75rem",
    marginTop: "1.5rem",
    paddingTop: "0.5rem",
  },
  cardBody: {
    padding: "16px",
  },
  detailsCard: {
    backgroundColor: "#fff",
    width: "100%",
    boxSizing: "border-box",
    marginTop: "1rem",
    borderRadius: 0,
  },
  cardHeaderWithMenu: {
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  detailsBody: {
    padding: "20px 24px",
  },
  inconsistencyBanner: {
    backgroundColor: "#FFD9D9",
    border: "none",
    borderRadius: 0,
    padding: "10px 16px",
    marginBottom: "1rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  inconsistencyDetailsLink: {
    backgroundColor: "transparent",
    border: "none",
    color: "#007BC0",
    fontWeight: 600,
    fontSize: "0.8125rem",
    cursor: "pointer",
    padding: 0,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  sideBySideCardsRow: {
    display: "flex",
    gap: "1rem",
    marginBottom: "1rem",
    width: "100%",
    boxSizing: "border-box",
    "@media (max-width: 960px)": {
      flexDirection: "column",
    },
  },
  leftRouteCard: {
    flex: "1.25",
    border: "1px solid #e0e0e0",
    borderRadius: 0,
    backgroundColor: "#fff",
    padding: "16px",
    boxSizing: "border-box",
    minWidth: 0,
  },
  rightCustomerCard: {
    flex: "1",
    border: "1px solid #e0e0e0",
    borderRadius: 0,
    backgroundColor: "#fff",
    padding: "16px",
    boxSizing: "border-box",
    minWidth: 0,
  },
  bottomGeneralCard: {
    border: "1px solid #e0e0e0",
    borderRadius: 0,
    backgroundColor: "#fff",
    padding: "16px",
    width: "100%",
    boxSizing: "border-box",
    marginBottom: "1rem",
  },
  sectionTitle: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "#000000",
    textTransform: "none",
    marginBottom: "1rem",
  },
  singleColumnFieldList: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    width: "100%",
  },
  detailsGrid4Cols: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "1.25rem 1.5rem",
    width: "100%",
    boxSizing: "border-box",
    "@media (max-width: 1200px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    boxSizing: "border-box",
  },
  fieldLabel: {
    fontSize: "0.75rem",
    color: "#555555",
    fontWeight: 500,
    marginBottom: "4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fieldInputWrapper: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #c1c7cc",
    paddingBottom: "2px",
    transition: "border-color 0.2s",
  },
  fieldInputWrapperFocused: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #007BC0",
    paddingBottom: "2px",
    transition: "border-color 0.2s",
  },
  fieldInput: {
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    flex: 1,
    fontSize: "0.875rem",
    color: "#000000",
    minWidth: 0,
    padding: "4px 0",
  },
  readOnlyFieldInput: {
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    flex: 1,
    fontSize: "0.875rem",
    color: "#444444",
    minWidth: 0,
    padding: "4px 0",
    cursor: "default",
  },
  fieldInputWrapperError: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#E0E2E5",
    border: "none",
    borderRadius: 0,
    padding: "2px 6px",
    boxSizing: "border-box",
  },
  fieldErrorMessageWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#FFD9D9",
    border: "none",
    borderRadius: 0,
    padding: "4px 8px",
    marginTop: "4px",
    boxSizing: "border-box",
  },
  fieldErrorIcon: {
    width: "14px",
    height: "14px",
    flexShrink: 0,
  },
  fieldErrorMessageText: {
    fontSize: "0.75rem",
    fontWeight: "normal",
    color: "#000000",
  },
  centerContainer: {
    minHeight: "15rem",
    padding: "2rem",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "0.75rem",
  },
  icon: {
    fontSize: "2rem",
    color: "#007bc0",
  },
  message: {
    fontSize: "0.95rem",
    color: "#555",
  },
});

function CasePaymentOrders({ caseId }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contract = useSelector((state) => state.contract?.contract);

  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [paymentOrders, setPaymentOrders] = useState([]);
  const [caseHistory, setCaseHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTab, setHistoryTab] = useState("general");

  const loadData = useCallback(() => {
    if (!caseId) {
      setTickets([]);
      setPaymentOrders([]);
      setCaseHistory([]);
      setLoading(false);
      setHistoryLoading(false);
      return;
    }

    setLoading(true);
    setHistoryLoading(true);

    const fetchTicketsPromise = baseRequest({ param: caseId }, dispatch, QUERY_GET_BILLING_TICKETS_BY_CASE_ID, "GET");
    const fetchOrdersPromise = baseRequest({ param: `${caseId}/payment-orders` }, dispatch, QUERY_GET_BILLING_PAYMENT_ORDERS, "GET");
    const fetchHistoryPromise = baseRequest({ param: `${caseId}/history` }, dispatch, QUERY_GET_BILLING_CASE_HISTORY, "GET");

    Promise.all([fetchTicketsPromise, fetchOrdersPromise, fetchHistoryPromise])
      .then(([ticketsData, ordersData, historyData]) => {
        setTickets(Array.isArray(ticketsData) ? ticketsData : []);
        setPaymentOrders(Array.isArray(ordersData) ? ordersData : []);
        setCaseHistory(Array.isArray(historyData) ? historyData : []);
        setLoading(false);
        setHistoryLoading(false);
      })
      .catch(() => {
        setTickets([]);
        setPaymentOrders([]);
        setCaseHistory([]);
        setLoading(false);
        setHistoryLoading(false);
      });
  }, [caseId, dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const [ticketFormData, setTicketFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  const [savingTicket, setSavingTicket] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [copiedDetailsField, setCopiedDetailsField] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [approvingOrder, setApprovingOrder] = useState(null);
  const [rejectingOrder, setRejectingOrder] = useState(null);

  const handleOrderSaved = (result, isEdit) => {
    dispatch(
      openModal({
        message: isEdit
          ? t("BillingPaymentOrders.SuccessEditOrder")
          : t("BillingPaymentOrders.SuccessCreateOrder"),
        type: "success",
      })
    );
    loadData();
  };

  const getFortnight = (date) => {
    if (!date) return "-";
    const d = dayjs(date);
    return d.isValid() ? (d.date() <= 15 ? "1Q" : "2Q") : "-";
  };

  const getInvoiceMonth = (date) => {
    if (!date) return "-";
    const d = dayjs(date);
    return d.isValid() ? d.format("MM/YYYY") : "-";
  };

  useEffect(() => {
    if (tickets && tickets.length > 0) {
      const tItem = tickets[0];
      const modelDesc = contract?.model
        ? (contract?.brand ? `${contract.brand} ${contract.model}` : contract.model)
        : (contract?.brand || "-");

      const initial = {
        customerName: tItem.customerName || contract?.customerName || "",
        vehicleModel: modelDesc && modelDesc !== "-" ? modelDesc : "",
        vehiclePlate: tItem.vehiclePlate || contract?.plate || "",
        vehicleCondition: tItem.vehicleCondition && tItem.vehicleCondition !== "-" ? tItem.vehicleCondition : "",
        serviceAddress: tItem.serviceAddress || "",
        serviceAddressNumber: tItem.serviceAddressNumber || "",
        serviceCity: tItem.serviceCity || "",
        serviceState: tItem.serviceState || "",
        serviceLocalDistance: tItem.serviceLocalDistance && tItem.serviceLocalDistance !== "-" ? tItem.serviceLocalDistance : "",
        destinationAddress: tItem.destinationAddress || "",
        destinationAddressNumber: tItem.destinationAddressNumber || "",
        destinationCity: tItem.destinationCity || "",
        destinationState: tItem.destinationState || "",
        serviceDistance: tItem.serviceDistance != null ? String(tItem.serviceDistance) : "",
        supplierName: tItem.supplierName && tItem.supplierName !== "-" ? tItem.supplierName : "",
        mspid: tItem.mspid && tItem.mspid !== "-" ? tItem.mspid : "",
        supplierProtocol: tItem.supplierProtocol || "",
        startDate: tItem.startDate ? dayjs(tItem.startDate).format("DD/MM/YYYY HH:mm") : "",
        orderDate: tItem.startDate ? dayjs(tItem.startDate).format("DD/MM/YYYY") : "",
        originDate: tItem.startDate ? dayjs(tItem.startDate).format("DD/MM/YYYY") : "",
        serviceLocalDate: tItem.serviceLocalDate && tItem.serviceLocalDate !== "-" ? tItem.serviceLocalDate : "",
        destinationDate: tItem.endDate ? dayjs(tItem.endDate).format("DD/MM/YYYY") : "",
        locationCondition: tItem.locationCondition && tItem.locationCondition !== "-" ? tItem.locationCondition : "",
        fortnight: getFortnight(tItem.startDate),
        invoiceMonth: getInvoiceMonth(tItem.startDate),
        franchiseKm: tItem.franchiseKm != null ? String(tItem.franchiseKm) : "",
        excessKm: tItem.excessKm != null ? String(tItem.excessKm) : "",
        baseRate: tItem.baseRate != null ? String(tItem.baseRate) : "",
        excessKmRate: tItem.excessKmRate != null ? String(tItem.excessKmRate) : "",
        supplierServiceTypeDescription: tItem.supplierServiceTypeDescription && tItem.supplierServiceTypeDescription !== "-" ? tItem.supplierServiceTypeDescription : "",
        supplierStatus: tItem.supplierStatus || "",
        boschStatus: tItem.boschStatus || "",
        financialStatus: tItem.financialStatus || "",
        inconsistencies: tItem.inconsistencies || "",
        agent: tItem.agent && tItem.agent !== "-" ? tItem.agent : "",
      };
      setTicketFormData(initial);
      setInitialFormData(initial);
    }
  }, [tickets, contract]);

  const hasChanges = useMemo(() => {
    if (!initialFormData || Object.keys(initialFormData).length === 0) return false;
    return Object.keys(ticketFormData).some((key) => {
      return (ticketFormData[key] || "") !== (initialFormData[key] || "");
    });
  }, [ticketFormData, initialFormData]);

  const handleCancelTicketChanges = () => {
    setTicketFormData(initialFormData);
  };

  const handleSaveTicketChanges = () => {
    const primary = tickets && tickets.length > 0 ? tickets[0] : null;
    if (!primary || !primary.id) return;

    setSavingTicket(true);

    const parseNum = (val) => {
      if (val === "" || val == null) return null;
      const num = Number(val);
      return isNaN(num) ? null : num;
    };

    const maxAllowed = 99999999.99;
    const baseRateVal = parseNum(ticketFormData.baseRate);
    const excessKmRateVal = parseNum(ticketFormData.excessKmRate);
    const serviceDistanceVal = parseNum(ticketFormData.serviceDistance);

    if (baseRateVal !== null && (baseRateVal < 0 || baseRateVal > maxAllowed)) {
      setSavingTicket(false);
      dispatch(
        openModal({
          message: t("TicketDetails.ErrorValueOutOfRange"),
          type: "error",
        })
      );
      return;
    }

    if (excessKmRateVal !== null && (excessKmRateVal < 0 || excessKmRateVal > maxAllowed)) {
      setSavingTicket(false);
      dispatch(
        openModal({
          message: t("TicketDetails.ErrorValueOutOfRange"),
          type: "error",
        })
      );
      return;
    }

    if (serviceDistanceVal !== null && (serviceDistanceVal < 0 || serviceDistanceVal > maxAllowed)) {
      setSavingTicket(false);
      dispatch(
        openModal({
          message: t("TicketDetails.ErrorValueOutOfRange"),
          type: "error",
        })
      );
      return;
    }

    const franchiseKmClean = String(ticketFormData.franchiseKm || "").replace(",", ".").trim();
    const franchiseKmVal = !isNaN(Number(franchiseKmClean)) && franchiseKmClean !== "" && franchiseKmClean !== "-" ? Number(franchiseKmClean) : null;

    const excessKmClean = String(ticketFormData.excessKm || "").replace(",", ".").trim();
    const excessKmVal = !isNaN(Number(excessKmClean)) && excessKmClean !== "" && excessKmClean !== "-" ? Number(excessKmClean) : null;

    const payload = {
      supplierProtocol: ticketFormData.supplierProtocol || null,
      serviceDistance: serviceDistanceVal,
      baseRate: baseRateVal,
      excessKmRate: excessKmRateVal,
      serviceCity: ticketFormData.serviceCity || null,
      serviceState: ticketFormData.serviceState || null,
      serviceAddress: ticketFormData.serviceAddress || null,
      serviceAddressNumber: ticketFormData.serviceAddressNumber || null,
      destinationCity: ticketFormData.destinationCity || null,
      destinationState: ticketFormData.destinationState || null,
      destinationAddress: ticketFormData.destinationAddress || null,
      destinationAddressNumber: ticketFormData.destinationAddressNumber || null,
      franchiseKm: franchiseKmVal,
      excessKm: excessKmVal,
      supplierStatus: ticketFormData.supplierStatus || null,
      boschStatus: ticketFormData.boschStatus || null,
      inconsistencies: ticketFormData.inconsistencies || null,
      financialStatus: ticketFormData.financialStatus || null,
    };

    baseRequest(
      { param: String(primary.id), body: payload },
      dispatch,
      QUERY_PUT_BILLING_TICKET,
      "PUT"
    )
      .then(() => {
        setSavingTicket(false);
        setInitialFormData(ticketFormData);
        dispatch(
          openModal({
            message: t("TicketDetails.SuccessUpdate"),
            type: "success",
          })
        );
        loadData();
      })
      .catch(() => {
        setSavingTicket(false);
        dispatch(
          openModal({
            message: t("TicketDetails.ErrorUpdate"),
            type: "error",
          })
        );
      });
  };

  const handleFieldChange = (key, value) => {
    setTicketFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleCopyDetail = (key, value) => {
    if (!value) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value);
    }
    setCopiedDetailsField(key);
    setTimeout(() => {
      setCopiedDetailsField(null);
    }, 2000);
  };

  const renderApprovalStatusBadge = (status) => {
    const s = status ? status.toUpperCase() : "PENDING";
    let bgColor = "#FFDF95";
    let text = t("BillingPaymentOrders.StatusPending");

    if (s === "APPROVED") {
      bgColor = "#B8EFC9";
      text = t("BillingPaymentOrders.StatusApproved");
    } else if (s === "REJECTED") {
      bgColor = "#FCE8E6";
      text = t("BillingPaymentOrders.StatusRejected");
    } else if (s === "CONTESTED") {
      bgColor = "#FFE0B2";
      text = t("BillingPaymentOrders.StatusContested");
    }

    return (
      <span
        style={{
          backgroundColor: bgColor,
          color: "#000000",
          borderRadius: "12px",
          padding: "0.2rem 0.6rem",
          fontSize: "0.75rem",
          fontWeight: 500,
        }}
      >
        {text}
      </span>
    );
  };

  const opStatusOptions = useMemo(
    () => [
      { id: "PENDING", text: t("BillingPaymentOrders.StatusPending") },
      { id: "APPROVED", text: t("BillingPaymentOrders.StatusApproved") },
      { id: "REJECTED", text: t("BillingPaymentOrders.StatusRejected") },
      { id: "CONTESTED", text: t("BillingPaymentOrders.StatusContested") },
    ],
    [t]
  );

  const getRubricLabel = useCallback(
    (type) => {
      switch (type) {
        case "BASE_RATE":
          return t("BillingPaymentOrders.RubricBaseRate");
        case "EXCESS_KM":
          return t("BillingPaymentOrders.RubricExcessKm");
        case "TOLL":
          return t("BillingPaymentOrders.RubricToll");
        case "STOP_HOUR":
          return t("BillingPaymentOrders.RubricStopHour");
        case "SKATES":
          return t("BillingPaymentOrders.RubricSkates");
        case "DISPATCH_FEE":
          return t("BillingPaymentOrders.RubricDispatchFee");
        case "OTHER":
          return t("BillingPaymentOrders.RubricOther");
        default:
          return type;
      }
    },
    [t]
  );

  const opTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(
          paymentOrders
            .map((po) => po.description || po.rubricType)
            .filter(Boolean)
        )
      ).map((type) => ({ id: type, text: getRubricLabel(type) || type })),
    [paymentOrders, getRubricLabel]
  );

  const formattedGeneralHistory = useMemo(() => {
    return (caseHistory || []).map((h) => ({
      id: h.id,
      date: h.changedOn ? dayjs(h.changedOn).format("DD/MM/YYYY") : "-",
      time: h.changedOn ? dayjs(h.changedOn).format("HH:mm:ss") : "-",
      userName: h.authorName || h.changedBy || "",
      category: h.category,
      action: h.action,
      opType: getRubricLabel(h.rubricType) || h.rubricType,
      quantity: h.quantity,
      unitValue: h.unitPrice,
      totalValue: h.totalValue,
      changesSummary: h.changesSummary,
      comment: h.rejectionReason || h.discountComment || h.doNotChargeReason || "",
    }));
  }, [caseHistory, getRubricLabel]);

  const formattedApprovalHistory = useMemo(() => {
    return (caseHistory || [])
      .filter(
        (h) =>
          (h.category || "").toUpperCase() === "APPROVAL" ||
          (h.action || "").toUpperCase() === "APPROVED" ||
          (h.action || "").toUpperCase() === "REJECTED"
      )
      .map((h) => ({
        id: h.id,
        date: h.changedOn ? dayjs(h.changedOn).format("DD/MM/YYYY") : "-",
        time: h.changedOn ? dayjs(h.changedOn).format("HH:mm:ss") : "-",
        userName: h.authorName || h.changedBy || "",
        action: h.action,
        opType: getRubricLabel(h.rubricType) || h.rubricType,
        unitValue: h.unitPrice,
        totalValue: h.totalValue,
        comment: h.rejectionReason || h.discountComment || h.doNotChargeReason || "",
      }));
  }, [caseHistory, getRubricLabel]);

  const filteredPaymentOrders = useMemo(() => {
    return paymentOrders.filter((po) => {
      if (selectedStatuses && selectedStatuses.length > 0) {
        const s = (po.approvalStatus || "PENDING").toUpperCase();
        if (!selectedStatuses.includes(s)) return false;
      }
      if (selectedTypes && selectedTypes.length > 0) {
        const type = po.description || po.rubricType;
        if (!selectedTypes.includes(type)) return false;
      }
      if (filterSearch && filterSearch.trim()) {
        const q = filterSearch.trim().toLowerCase();
        const desc = (po.description || "").toLowerCase();
        const rubric = (po.rubricType || "").toLowerCase();
        const unit = String(po.unitPrice || "");
        const total = String(po.totalValue || "");
        if (
          !desc.includes(q) &&
          !rubric.includes(q) &&
          !unit.includes(q) &&
          !total.includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [paymentOrders, selectedStatuses, selectedTypes, filterSearch]);

  const validatedFieldKeys = useMemo(() => [
    "serviceAddress",
    "serviceAddressNumber",
    "destinationAddress",
    "destinationAddressNumber",
    "customerName",
    "vehicleModel",
    "vehiclePlate",
    "vehicleCondition",
    "supplierName",
    "mspid",
    "orderDate",
    "serviceCity",
    "fortnight",
    "baseRate",
    "destinationDate",
    "locationCondition",
    "originDate",
    "supplierProtocol",
    "invoiceMonth",
    "serviceLocalDistance",
    "serviceState",
    "serviceLocalDate",
    "serviceDistance",
    "franchiseKm",
    "excessKm",
    "startDate",
    "excessKmRate",
    "supplierServiceTypeDescription",
    "supplierStatus",
    "boschStatus",
    "financialStatus",
    "agent",
    "destinationCity",
    "destinationState",
  ], []);

  const numericFieldKeys = useMemo(() => [
    "baseRate",
    "excessKmRate",
    "serviceDistance",
    "franchiseKm",
    "excessKm",
    "serviceLocalDistance",
  ], []);

  const fieldErrors = useMemo(() => {
    if (!ticketFormData || Object.keys(ticketFormData).length === 0) {
      return {};
    }

    const errors = {};

    const isEmpty = (val) => {
      return val === undefined || val === null || String(val).trim() === "" || String(val).trim() === "-";
    };

    const isNumeric = (val) => {
      if (isEmpty(val)) return false;
      const clean = String(val).replace(",", ".").trim();
      const n = Number(clean);
      return !isNaN(n) && isFinite(n);
    };

    validatedFieldKeys.forEach((key) => {
      const val = ticketFormData[key];
      if (isEmpty(val)) {
        errors[key] = {
          type: "required",
          message: t("TicketDetails.ErrorFieldMissing"),
        };
      } else if (numericFieldKeys.includes(key)) {
        const numVal = Number(String(val).replace(",", "."));
        if (!isNumeric(val) || numVal < 0 || (key === "franchiseKm" && numVal <= 0)) {
          errors[key] = {
            type: "invalid",
            message: t("TicketDetails.ErrorFieldInvalid"),
          };
        }
      }
    });

    if (
      ticketFormData?.inconsistencies &&
      ticketFormData.inconsistencies.trim() &&
      ticketFormData.inconsistencies !== "-" &&
      ticketFormData.inconsistencies.toLowerCase() !== "nenhuma inconsistência"
    ) {
      const incText = ticketFormData.inconsistencies.toLowerCase();

      if (incText.includes("tarifa") || incText.includes("saída") || incText.includes("base")) {
        if (!errors.baseRate && (!isNumeric(ticketFormData.baseRate) || Number(String(ticketFormData.baseRate).replace(",", ".")) <= 0)) {
          errors.baseRate = {
            type: "invalid",
            message: t("TicketDetails.ErrorFieldInvalid"),
          };
        }
      }

      if (incText.includes("distância") || incText.includes("distancia") || incText.includes("km total")) {
        if (!errors.serviceDistance && (!isNumeric(ticketFormData.serviceDistance) || Number(String(ticketFormData.serviceDistance).replace(",", ".")) <= 0)) {
          errors.serviceDistance = {
            type: "invalid",
            message: t("TicketDetails.ErrorFieldInvalid"),
          };
        }
      }

      if (incText.includes("excedente") || incText.includes("km excedente")) {
        if (!errors.excessKmRate && (!isNumeric(ticketFormData.excessKmRate) || Number(String(ticketFormData.excessKmRate).replace(",", ".")) < 0)) {
          errors.excessKmRate = {
            type: "invalid",
            message: t("TicketDetails.ErrorFieldInvalid"),
          };
        }
      }
    }

    return errors;
  }, [ticketFormData, validatedFieldKeys, numericFieldKeys, t]);

  const inconsistencyCount = useMemo(() => {
    const errorCount = Object.keys(fieldErrors).length;
    if (errorCount > 0) {
      return errorCount;
    }
    if (
      !ticketFormData?.inconsistencies ||
      ticketFormData.inconsistencies === "-" ||
      ticketFormData.inconsistencies.toLowerCase() === "nenhuma inconsistência"
    ) {
      return 0;
    }
    const parts = ticketFormData.inconsistencies
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length > 0 ? parts.length : 1;
  }, [fieldErrors, ticketFormData?.inconsistencies]);

  const hasInconsistencies = inconsistencyCount > 0;

  if (loading) {
    return (
      <div className={classes.centerContainer}>
        <CircularProgress size={32} />
      </div>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <div className={classes.centerContainer}>
        <i className={`icon icon-alert-info ${classes.icon}`} />
        <span className={classes.message}>{t("CaseTabs.NoPaymentOrders")}</span>
      </div>
    );
  }

  const primaryTicket = tickets && tickets.length > 0 ? tickets[0] : null;

  const totalOrdersCount = paymentOrders.length;
  const totalValue =
    paymentOrders.length > 0
      ? paymentOrders.reduce((sum, po) => sum + (Number(po.totalValue) || 0), 0)
      : primaryTicket?.totalValue || 0;
  const pendingValue = paymentOrders
    .filter((po) => !po.approvalStatus || po.approvalStatus.toUpperCase() === "PENDING")
    .reduce((sum, po) => sum + (Number(po.totalValue) || 0), 0);
  const rejectedValue = paymentOrders
    .filter((po) => po.approvalStatus && po.approvalStatus.toUpperCase() === "REJECTED")
    .reduce((sum, po) => sum + (Number(po.totalValue) || 0), 0);

  const vehicleModel = contract?.model
    ? (contract?.brand ? `${contract.brand} ${contract.model}` : contract.model)
    : (contract?.brand || "-");

  const bigNumbers = [
    {
      key: "customer",
      title: t("Customer"),
      value: primaryTicket?.customerName || contract?.customerName || "-",
      isBlue: true,
    },
    {
      key: "supplier",
      title: t("Supplier"),
      value: primaryTicket?.supplierName || "-",
      isBlue: true,
    },
    {
      key: "vehicleModel",
      title: t("BillingPaymentOrders.SummaryVehicleModel"),
      value: vehicleModel,
      isBlue: true,
    },
    {
      key: "totalOrders",
      title: t("BillingPaymentOrders.SummaryTotalOrders"),
      value: String(totalOrdersCount),
      isBlue: false,
      icon: clipboardListIcon,
    },
    {
      key: "totalValue",
      title: t("BillingPaymentOrders.SummaryTotalValue"),
      value: `R$ ${Number(totalValue).toFixed(2)}`,
      isBlue: false,
    },
    {
      key: "pendingValue",
      title: t("BillingPaymentOrders.SummaryPendingValue"),
      value: `R$ ${Number(pendingValue).toFixed(2)}`,
      isBlue: false,
    },
    {
      key: "rejectedValue",
      title: t("BillingPaymentOrders.SummaryRejectedValue"),
      value: `R$ ${Number(rejectedValue).toFixed(2)}`,
      isBlue: false,
    },
  ];

  const routeCardFields = [
    { key: "serviceAddress", label: t("TicketDetails.ServiceAddress"), readOnly: false },
    { key: "serviceAddressNumber", label: t("TicketDetails.ServiceAddressNumber"), readOnly: false },
    { key: "destinationAddress", label: t("TicketDetails.DestinationAddress"), readOnly: false },
    { key: "destinationAddressNumber", label: t("TicketDetails.DestinationAddressNumber"), readOnly: false },
  ];

  const customerCardFields = [
    { key: "customerName", label: t("TicketDetails.CustomerName"), readOnly: true },
    { key: "vehicleModel", label: t("TicketDetails.VehicleModel"), readOnly: true },
    { key: "vehiclePlate", label: t("TicketDetails.VehiclePlate"), readOnly: false },
    { key: "vehicleCondition", label: t("TicketDetails.VehicleCondition"), readOnly: false },
  ];

  const otherTicketInfoFields = [
    { key: "supplierName", label: t("TicketDetails.Supplier"), readOnly: false },
    { key: "mspid", label: t("TicketDetails.MspId"), readOnly: false },
    { key: "orderDate", label: t("TicketDetails.OrderDate"), readOnly: false },
    { key: "serviceCity", label: t("TicketDetails.ServiceCity"), readOnly: false },

    { key: "fortnight", label: t("TicketDetails.Fortnight"), readOnly: false },
    { key: "baseRate", label: t("TicketDetails.DepartureValue"), readOnly: false },
    { key: "destinationDate", label: t("TicketDetails.DestinationDate"), readOnly: false },
    { key: "locationCondition", label: t("TicketDetails.LocationCondition"), readOnly: false },

    { key: "originDate", label: t("TicketDetails.OriginDate"), readOnly: false },
    { key: "supplierProtocol", label: t("TicketDetails.OrderNumber"), readOnly: false },
    { key: "invoiceMonth", label: t("TicketDetails.InvoiceMonth"), readOnly: false },
    { key: "serviceLocalDistance", label: t("TicketDetails.ServiceLocalDistance"), readOnly: false },

    { key: "serviceState", label: t("TicketDetails.ServiceState"), readOnly: false },
    { key: "serviceLocalDate", label: t("TicketDetails.ServiceLocalDate"), readOnly: false },
    { key: "serviceDistance", label: t("TicketDetails.TotalDistance"), readOnly: false },

    { key: "franchiseKm", label: t("TicketDetails.FranchiseKm"), readOnly: false },
    { key: "excessKm", label: t("TicketDetails.ExcessKm"), readOnly: false },
    { key: "startDate", label: t("TicketDetails.TicketOpenDate"), readOnly: true },

    { key: "excessKmRate", label: t("TicketDetails.ExcessKmRate"), readOnly: false },
    { key: "supplierServiceTypeDescription", label: t("TicketDetails.ServiceType"), readOnly: false },
    { key: "supplierStatus", label: t("TicketDetails.SupplierStatus"), readOnly: true },
    { key: "boschStatus", label: t("TicketDetails.BoschStatus"), readOnly: true },
    { key: "financialStatus", label: t("TicketDetails.FinancialStatus"), readOnly: true },
    { key: "inconsistencies", label: t("TicketDetails.Inconsistencies"), readOnly: false },
    { key: "agent", label: t("TicketDetails.Agent"), readOnly: true },
    { key: "destinationCity", label: t("TicketDetails.DestinationCity"), readOnly: false },
    { key: "destinationState", label: t("TicketDetails.DestinationState"), readOnly: false },
  ];

  const renderDetailField = (field) => {
    const value = ticketFormData[field.key] || "";
    const isFocused = focusedField === field.key;
    const fieldError = fieldErrors[field.key];

    let wrapperClass = classes.fieldInputWrapper;
    if (fieldError) {
      wrapperClass = classes.fieldInputWrapperError;
    } else if (isFocused) {
      wrapperClass = classes.fieldInputWrapperFocused;
    }

    return (
      <div key={field.key} className={classes.fieldContainer}>
        <span className={classes.fieldLabel}>{field.label}</span>
        <div className={wrapperClass}>
          <input
            type="text"
            className={field.readOnly ? classes.readOnlyFieldInput : classes.fieldInput}
            value={value}
            maxLength={["baseRate", "excessKmRate", "serviceDistance"].includes(field.key) ? 12 : undefined}
            onChange={(e) => !field.readOnly && handleFieldChange(field.key, e.target.value)}
            onFocus={() => !field.readOnly && setFocusedField(field.key)}
            onBlur={() => setFocusedField(null)}
            readOnly={Boolean(field.readOnly)}
          />
          <Tooltip
            title={
              copiedDetailsField === field.key
                ? t("TicketInfo.Copied")
                : t("TicketInfo.Copy")
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={!value || value === "-"}
                onClick={() => handleCopyDetail(field.key, value)}
                style={{ padding: "4px", marginLeft: "4px", flexShrink: 0 }}
                aria-label={`${t("TicketInfo.Copy")} ${field.label}`}
              >
                <img
                  src={copyIcon}
                  alt=""
                  style={{ width: "1rem", height: "1rem" }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        {fieldError && (
          <div className={classes.fieldErrorMessageWrapper}>
            <img
              src={uiIcAlertError}
              alt=""
              className={classes.fieldErrorIcon}
            />
            <span className={classes.fieldErrorMessageText}>
              {fieldError.message}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.bigNumbersWrapper}>
        {bigNumbers.map((item) => (
          <div
            key={item.key}
            className={item.isBlue ? classes.bigNumberCardBlue : classes.bigNumberCardWhite}
            title={`${item.title}: ${item.value}`}
          >
            <span className={item.isBlue ? classes.bigNumberTitleBlue : classes.bigNumberTitleWhite}>
              {item.title}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", minWidth: 0 }}>
              {item.icon && (
                <img
                  src={item.icon}
                  alt=""
                  style={{ width: "1rem", height: "1rem", flexShrink: 0 }}
                />
              )}
              <span className={item.isBlue ? classes.bigNumberValueBlue : classes.bigNumberValueWhite}>
                {item.value}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className={classes.tableCard}>
        <div className={classes.cardHeader}>
          <span className={classes.cardTitle}>{t("CaseTabs.PaymentOrders")}</span>
        </div>
        <div className={classes.cardDivider} />
        <div className={classes.tableToolbar}>
          <div style={{ minWidth: "180px", maxWidth: "260px", flex: "1 1 200px" }}>
            <TextBox
              className={classes.compactToolbarInput}
              height={38}
              label={`${t("Search")}...`}
              value={filterSearch}
              mode="text"
              onValueChange={(val) => setFilterSearch(val)}
              valueChangeEvent="keyup"
              showClearButton={true}
            >
              <TextBoxButton
                name="search"
                location="after"
                options={{
                  icon: searchIcon,
                  stylingMode: "text",
                }}
              />
            </TextBox>
          </div>

          <div style={{ minWidth: "160px", maxWidth: "230px", flex: "1 1 180px" }}>
            <TagBox
              className={classes.compactToolbarInput}
              height={38}
              label={t("BillingPaymentOrders.FilterStatus")}
              labelMode="floating"
              dataSource={opStatusOptions}
              displayExpr="text"
              valueExpr="id"
              value={selectedStatuses}
              onValueChanged={(e) => setSelectedStatuses(e.value || [])}
              showSelectionControls={true}
              multiline={false}
              applyValueMode="instantly"
              showClearButton={true}
            />
          </div>

          <div style={{ minWidth: "160px", maxWidth: "230px", flex: "1 1 180px" }}>
            <TagBox
              className={classes.compactToolbarInput}
              height={38}
              label={t("BillingPaymentOrders.FilterType")}
              labelMode="floating"
              dataSource={opTypeOptions}
              displayExpr="text"
              valueExpr="id"
              value={selectedTypes}
              onValueChanged={(e) => setSelectedTypes(e.value || [])}
              showSelectionControls={true}
              multiline={false}
              applyValueMode="instantly"
              showClearButton={true}
            />
          </div>

          <div style={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            color="primary"
            className={classes.addOrderButton}
            onClick={() => setIsCreateModalOpen(true)}
            startIcon={<img src={addIconWhite} alt="" style={{ width: "1rem", height: "1rem" }} />}
          >
            {t("BillingPaymentOrders.BtnAddOrder")}
          </Button>
        </div>
        <div className={classes.cardBody}>
          <DataGrid
            dataSource={filteredPaymentOrders}
            showBorders={true}
            rowAlternationEnabled={true}
            hoverStateEnabled={true}
            noDataText={t("NoDataToDisplay")}
            width="100%"
          >
            <Column
              dataField="id"
              caption={t("BillingPaymentOrders.ColId")}
              width={"7%"}
              cellRender={(row) => row.value || "-"}
            />
            <Column
              dataField="description"
              caption={t("BillingPaymentOrders.ColOrderType")}
              cellRender={(row) => getRubricLabel(row.value) || getRubricLabel(row.data?.rubricType) || "-"}
            />
            <Column
              dataField="quantity"
              caption={t("BillingPaymentOrders.ColQuantity")}
              width={"7%"}
              cellRender={(row) => (row.value != null ? Number(row.value) : "-")}
            />
            <Column
              dataField="unitPrice"
              caption={t("BillingPaymentOrders.ColUnitPrice")}
              width={"11%"}
              cellRender={(row) => (row.value != null ? `R$ ${Number(row.value).toFixed(2)}` : "-")}
            />
            <Column
              dataField="totalValue"
              caption={t("BillingPaymentOrders.ColTotalValue")}
              width={"11%"}
              cellRender={(row) => (row.value != null ? `R$ ${Number(row.value).toFixed(2)}` : "-")}
            />
            <Column
              dataField="salePrice"
              caption={t("BillingPaymentOrders.ColSalePrice")}
              width={"11%"}
              cellRender={(row) => (row.value != null ? `R$ ${Number(row.value).toFixed(2)}` : "R$ 0.00")}
            />
            <Column
              dataField="createdOn"
              caption={t("BillingPaymentOrders.ColInclusionDate")}
              dataType="datetime"
              width={"14%"}
              cellRender={(row) => (row.value ? dayjs(convertToLocalTime(row.value)).format("DD/MM/YYYY HH:mm") : "-")}
            />
            <Column
              dataField="approvalStatus"
              caption={t("BillingPaymentOrders.ColApprovalStatus")}
              width={"11%"}
              cellRender={(row) => renderApprovalStatusBadge(row.value)}
            />
            <Column
              caption={t("BillingPaymentOrders.ColActions")}
              width={"11%"}
              cellRender={(row) => (
                <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                  <IconButton
                    size="small"
                    title={t("BillingPaymentOrders.BtnApprove")}
                    aria-label={t("BillingPaymentOrders.BtnApprove")}
                    style={{ padding: "4px" }}
                    onClick={() => setApprovingOrder(row.data)}
                  >
                    <img src={checkmarkFrameIcon} alt="" style={{ width: "1.25rem", height: "1.25rem" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title={t("BillingPaymentOrders.BtnReject")}
                    aria-label={t("BillingPaymentOrders.BtnReject")}
                    style={{ padding: "4px" }}
                    onClick={() => setRejectingOrder(row.data)}
                  >
                    <img src={abortFrameIcon} alt="" style={{ width: "1.25rem", height: "1.25rem" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title={t("BillingPaymentOrders.BtnEdit")}
                    aria-label={t("BillingPaymentOrders.BtnEdit")}
                    style={{ padding: "4px" }}
                    onClick={() => setEditingOrder(row.data)}
                  >
                    <img src={editIcon} alt="" style={{ width: "1.25rem", height: "1.25rem" }} />
                  </IconButton>
                </div>
              )}
            />
          </DataGrid>
        </div>
      </div>
      <div className={classes.detailsCard}>
        <div className={classes.cardHeaderWithMenu}>
          <span className={classes.cardTitle}>{t("TicketDetails.Title")}</span>
          <IconButton size="small" aria-label="more options">
            <MoreVertIcon style={{ color: "#000000" }} />
          </IconButton>
        </div>
        <div className={classes.cardDivider} />
        <div className={classes.detailsBody}>
          {hasInconsistencies && (
            <div className={classes.inconsistencyBanner}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <img src={uiIcAlertError} alt="" style={{ width: "1.125rem", height: "1.125rem" }} />
                <span style={{ fontWeight: "normal", fontSize: "0.875rem", color: "#000000" }}>
                  {inconsistencyCount === 1
                    ? t("TicketDetails.InconsistenciesBannerSingular", { count: 1 })
                    : t("TicketDetails.InconsistenciesBanner", { count: inconsistencyCount })}
                </span>
              </div>
              <button
                type="button"
                className={classes.inconsistencyDetailsLink}
                onClick={() => {}}
              >
                {t("TicketDetails.ViewDetails")} &gt;
              </button>
            </div>
          )}

          <div className={classes.sideBySideCardsRow}>
            <div className={classes.leftRouteCard}>
              <div className={classes.sectionTitle}>
                {t("TicketDetails.SectionRouteInfo")}
              </div>
              <div className={classes.singleColumnFieldList}>
                {routeCardFields.map((field) => renderDetailField(field))}
              </div>
            </div>

            <div className={classes.rightCustomerCard}>
              <div className={classes.sectionTitle}>
                {t("TicketDetails.SectionCustomerInfo")}
              </div>
              <div className={classes.singleColumnFieldList}>
                {customerCardFields.map((field) => renderDetailField(field))}
              </div>
            </div>
          </div>

          <div className={classes.bottomGeneralCard}>
            <div className={classes.sectionTitle}>
              {t("TicketDetails.SectionOtherTicketInfo")}
            </div>
            <div className={classes.detailsGrid4Cols}>
              {otherTicketInfoFields.map((field) => renderDetailField(field))}
            </div>
          </div>

          <div className={classes.detailsActions}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancelTicketChanges}
              disabled={!hasChanges || savingTicket}
              style={{ borderRadius: 0 }}
            >
              {t("TicketDetails.BtnCancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveTicketChanges}
              disabled={!hasChanges || savingTicket}
              style={{ borderRadius: 0 }}
            >
              {savingTicket ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("TicketDetails.BtnSave")
              )}
            </Button>
          </div>
        </div>
      </div>
      <div
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          boxSizing: "border-box",
          marginTop: "1rem",
          border: "1px solid #e0e0e0",
          borderRadius: 0,
        }}
      >
        <div style={{ borderBottom: "1px solid #e0e0e0", padding: "0 16px" }}>
          <Tabs
            value={historyTab}
            onChange={(e, newTab) => setHistoryTab(newTab)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              value="general"
              label={t("BillingPaymentOrders.TabGeneralHistory")}
              style={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: 0,
              }}
            />
            <Tab
              value="approvals"
              label={t("BillingPaymentOrders.TabApprovalHistory")}
              style={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                borderRadius: 0,
              }}
            />
          </Tabs>
        </div>

        {historyTab === "general" ? (
          <GeneralBillingHistory items={formattedGeneralHistory} loading={historyLoading} />
        ) : (
          <ApprovalHistory items={formattedApprovalHistory} loading={historyLoading} />
        )}
      </div>
      <CreatePaymentOrderModal
        open={isCreateModalOpen || Boolean(editingOrder)}
        caseId={caseId}
        orderToEdit={editingOrder}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingOrder(null);
        }}
        onSuccess={handleOrderSaved}
      />
      <ApprovePaymentOrderModal
        open={Boolean(approvingOrder)}
        order={approvingOrder}
        onClose={() => setApprovingOrder(null)}
        onSuccess={() => {
          setApprovingOrder(null);
          loadData();
        }}
      />
      <RejectPaymentOrderModal
        open={Boolean(rejectingOrder)}
        order={rejectingOrder}
        onClose={() => setRejectingOrder(null)}
        onSuccess={() => {
          setRejectingOrder(null);
          loadData();
        }}
      />
    </div>
  );
}

export { CasePaymentOrders };
